import {createSelector} from 'reselect';

import {PRESCRIPTION_METHOD, TRACKED_DETAILS} from 'constants/product';
import {getDrawerState} from 'desktop/pages/success/selectorsV2';
import {
    OrderedItemType,
    DoctorDetailsAndUploadValidatedType,
    SuccessScreenType,
    SuccessPageType,
} from 'desktop/pages/success/types/orderItemsTypes';
import {validateDrDetails} from 'desktop/pages/success/utils/validation/validation';
import {ApplicationStoreType} from 'desktop/store';

import {FieldType, DoctorDetailsItemType, BaseItemType} from 'types/successPage';

import {MODULE_KEY_SUCCESS} from '../../constants';

/**
 * Get success page
 * @param {ApplicationStoreType} store - store
 * @returns {SuccessPage} - success page
 */
export const getSuccessPageOrderItems = (store: ApplicationStoreType): SuccessPageType =>
    store[MODULE_KEY_SUCCESS].orderItems;

/**
 * Returns ordered items from ordered order
 * @param {ApplicationStoreType} store - store
 * @returns {Array<OrderedItemType>} - list of the ordered items
 */
export const getOrderedItems = (store: ApplicationStoreType): Array<OrderedItemType> =>
    getSuccessPageOrderItems(store).orderedItems;

/**
 * Determines if order has contact lenses items
 * @param {ApplicationStoreType} store - store
 * @param {Array<OrderedItemType>} orderedItems - orderedItems
 * @returns {boolean} flag if in order has contact lenses
 */
export const isOrderContainsSomeContactLenses = createSelector(getOrderedItems, items => {
    return items.length > 0 && items.some(({product}) => product.isContactLenses);
});

/**
 * Determines if order has items with subscription
 * @param {ApplicationStoreType} store - store
 * @param {Array<OrderedItemType>} orderedItems - orderedItems
 * @returns {boolean} flag if in order has items with subscription
 */
export const isOrderContainsSubscriptionItems = createSelector(getOrderedItems, items => {
    return items.length > 0 && items.some(({subscriptionMode}) => subscriptionMode);
});

/**
 * get success screen
 * @param {ApplicationStoreType} store - store
 * @returns {SuccessScreenType} - success screen
 */
export const getSuccessScreen = (store: ApplicationStoreType): SuccessScreenType =>
    getSuccessPageOrderItems(store).successScreen;

/**
 * This is active items with whom we work send later or fill online
 * @param {ApplicationStoreType} store - store
 * @returns {Array<FieldType>} - success page items
 */
export const getSuccessPageItems = (store: ApplicationStoreType): Array<FieldType> =>
    getSuccessPageOrderItems(store).items;

/**
 * If file or doctor details failed validation than will return true else false
 * @returns {Boolean} - validation check
 */
export const getSuccessPageItemsValidated = createSelector(
    getSuccessPageItems,
    (successPageItems): {items: DoctorDetailsAndUploadValidatedType; isAllValid: boolean} => {
        const items: DoctorDetailsAndUploadValidatedType = {};
        successPageItems.forEach(item => {
            if (item.sendBy === PRESCRIPTION_METHOD.SEND_EMAIL_OR_FAX_METHOD) {
                items[item.orderItemId] = {
                    upload: item.fileSrc ? '' : 'Please upload your prescription',
                };
            }

            if (item.sendBy === PRESCRIPTION_METHOD.FILL_IT_ONLINE_METHOD) {
                // @ts-expect-error
                items[item.orderItemId] = validateDrDetails(item as DoctorDetailsItemType);
            }
        });

        return {
            isAllValid: Object.values(items)
                .map(el => Object.values(el).every(error => error === ''))
                .every(result => result),
            items,
        };
    },
);

/**
 * If currently open items doctor details failed validation than will return true else false
 * @returns {Boolean} - validation check
 */
export const getDoctorDetailsValidated = createSelector(
    getDrawerState,
    getSuccessPageItems,
    ({item}, orderItems) => {
        if (!item) return {isAllValid: false, items: {}};
        const orderItem = orderItems.find(({orderItemId}) => orderItemId === String(item.id));
        const items: DoctorDetailsAndUploadValidatedType = {
            ...orderItems.reduce(
                (acc, {orderItemId}) => {
                    acc[orderItemId] = {};
                    return acc;
                },
                {} as Record<string, {}>,
            ),
            [item.id]: validateDrDetails(orderItem as DoctorDetailsItemType),
        };

        return {
            isAllValid: Object.values(items)
                .map(el => Object.values(el).every(error => error === ''))
                .every(result => result),
            items,
        };
    },
);

/**
 * Returns validated items from typ form
 * @param {ApplicationStoreType} store - store
 * @returns {DoctorDetailsAndUploadValidatedType} - validated items
 */
export const getValidatedItems = (
    store: ApplicationStoreType,
): DoctorDetailsAndUploadValidatedType => getSuccessPageOrderItems(store).itemsValidated;

/**
 * Returns error message
 * @param {ApplicationStoreType} store - store
 * @returns {string} - error message
 */
export const getErrorMessage = createSelector(
    getSuccessPageItemsValidated,
    getSuccessPageItems,
    ({isAllValid}, items) => {
        if (isAllValid) return '';
        const isOnlySendLater = items.every(
            ({sendBy}) => sendBy === PRESCRIPTION_METHOD.SEND_EMAIL_OR_FAX_METHOD,
        );
        if (isOnlySendLater) return 'Please upload your prescription';
        return 'Please fill in all fields';
    },
);

/**
 * Returns true or false
 * @param {ApplicationStoreType} store - store
 * @returns {boolean} - true or false
 */
export const isOnlyDoctorDetails = createSelector(getSuccessPageItems, items => {
    return items.every(({sendBy}) => sendBy === PRESCRIPTION_METHOD.FILL_IT_ONLINE_METHOD);
});

/**
 * get status of success page
 * @param {ApplicationStoreType} store - store
 * @returns {string} status - status of success page
 */
export const getStatus = (store: ApplicationStoreType): string =>
    getSuccessPageOrderItems(store).status;

/**
 * Get a list of unique values for sendBy
 * @returns {Array<number>} - list of unique values
 */
export const getUniqueSendBy = createSelector(getSuccessPageItems, items =>
    [...new Set(items.map(({sendBy}) => sendBy))].sort((a: number, b: number) => a - b),
);

/**
 * Get a string with sendBy values for the list of products
 * @returns {string} - message
 */
export const getSendByMessage = createSelector(getUniqueSendBy, (list: number[]) =>
    list
        .reduce((acc: string[], item: number) => {
            const detail = TRACKED_DETAILS[item];
            if (detail) return [...acc, detail];
            return acc;
        }, [])
        .join(' / '),
);

/**
 * It takes first item of array with existing doctorDetails prop
 * @returns {boolean} - first doctor details
 */
export const getIsAutocompleteModeEnabled = createSelector(getSuccessPageItems, items =>
    (items as (DoctorDetailsItemType & BaseItemType)[]).reduce(
        (acc, item) => {
            return {
                ...acc,
                [item.orderItemId]: item.isAutocompleteModeEnabled,
            };
        },
        {} as Record<string, boolean>,
    ),
);

/**
 * Gets success page item by orderItemId
 *  * @returns {string} - fileSrc
 */
export const getSuccessPageItem = createSelector(
    [getDrawerState, getSuccessPageItems],
    ({item}, items) => items.find(({orderItemId}) => orderItemId === String(item?.id)) || null,
);

/**
 * Get uploaded prescription file by orderItemId
 *  * @returns {string} - fileSrc
 */
export const getUploadedPrescriptionFile = (store: ApplicationStoreType): string =>
    getSuccessPageItem(store)?.fileSrc || '';

/**
 * Gets doctor details by orderItemId
 *  * @returns {string} - fileSrc
 */
export const getDoctorDetails = (store: ApplicationStoreType) =>
    getSuccessPageItem(store)?.doctorDetails || null;

/**
 * Gets visibly data for order
 * @returns {string} - fileSrc
 */
export const getVisiblyData = (store: ApplicationStoreType) =>
    getSuccessPageOrderItems(store).visiblyDetails;
